import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  MultiSelectFilter,
  FilterApplyButton,
  FILTER_BAR_PAPER_SX,
  FILTER_FIELD_SX,
  FILTER_LABEL_SX,
  FILTER_BAR_GAP,
  FILTER_BUTTON_SX,
} from '../../Common/DashboardKit';
import {
  useFieldSalesFilterOptions,
  useFieldSalesFilterState,
} from '../hooks/useFieldSalesDashboard.js';
import { useUrlFilters } from '../../../hooks/useUrlFilters.js';

const NO_DATA_TOOLTIP = 'No data source yet';

// Shared field wrapper (label above a control) — one source of truth so all
// four filter bars stay in sync.
const FIELD_SX = FILTER_FIELD_SX;

// ── URL ownership ────────────────────────────────────────────────────────────
// The Field Sales page OWNS exactly these query-param keys — the LIVE filters:
// fy (fiscal_year), months, division, state, brand, hq, bdm, zm. Months ride as
// YYYY-MM in the URL (?months=2026-08); FieldSalesApi converts to YYYYMM on the
// wire. There is NO month control — months are driven by the URL only. Zone,
// Category and DSH are NOT owned here (greyed, never read/written).
const FIELD_SALES_FILTER_KEYS = ['fy', 'months', 'division', 'state', 'brand', 'hq', 'bdm', 'zm'];

/** Split a comma-separated URL list into a trimmed, non-empty string[]. */
function splitList(raw) {
  if (!raw) return [];
  return String(raw).split(',').map((s) => s.trim()).filter(Boolean);
}

/** Join a string[] into a comma-separated URL value (null when empty). */
function joinList(list) {
  const arr = (list ?? []).filter(Boolean);
  return arr.length ? arr.join(',') : null;
}

/** Validate a UI month token "YYYY-MM" (URL-only; no month UI). */
function isMonthShape(value) {
  return /^\d{4}-\d{2}$/.test(String(value));
}

// Full Year (fiscal_year) option list from the reference, in its order. Only
// the two real fiscal years are live; the rest are greyed and never selectable.
const YEAR_OPTIONS = [
  'TGT', 'STK', 'SEC', 'SALES', 'RETURN', 'RATE DIFF', 'LY STK', 'LY SEC',
  'EXPIRY', 'BREAKAGE', '2026-2027', '2025-2026', '2024-2025', '2023-2024',
];
const YEAR_LIVE = new Set(['2026-2027', '2025-2026']);

/** Uppercase caption + small "No data" tag — matches the shared label style. */
function GreyedLabel({ label }) {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Typography sx={FILTER_LABEL_SX}>
        {label}
      </Typography>
      <Box
        component="span"
        sx={{
          px: '6px',
          py: '1px',
          borderRadius: '999px',
          bgcolor: '#eef1f5',
          border: '1px solid #E2E8F0',
          color: '#64748B',
          fontSize: '9px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '.3px',
          whiteSpace: 'nowrap',
        }}
      >
        No data
      </Box>
    </Box>
  );
}

/**
 * A greyed, inert filter control (no data source yet). Mirrors the shared
 * field skeleton — uppercase label + a disabled select-box showing "All" — but
 * at 50% opacity, not clickable, with a "No data source yet" tooltip and a
 * small "No data" tag by the label. Never sent in requests.
 */
function GreyedFilter({ label }) {
  return (
    <Tooltip title={NO_DATA_TOOLTIP} arrow>
      <Box sx={{ ...FIELD_SX, opacity: 0.5, cursor: 'not-allowed' }} aria-disabled="true">
        <GreyedLabel label={label} />
        <FormControl size="small" fullWidth disabled>
          <Select
            value=""
            displayEmpty
            IconComponent={undefined}
            renderValue={() => <Box component="span" sx={{ color: '#94A3B8' }}>All</Box>}
            sx={{ borderRadius: '8px', pointerEvents: 'none' }}
          >
            <MenuItem value="">All</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Tooltip>
  );
}

/** Dim non-live Year options (greyed, with a tooltip); live ones render plainly. */
function renderYearOption(option, { selected }) {
  const live = YEAR_LIVE.has(option);
  const text = (
    <Typography
      noWrap
      sx={{
        fontWeight: 600,
        fontSize: '12.5px',
        color: selected ? '#1D4ED8' : '#0F172A',
        ...(live ? {} : { opacity: 0.5 }),
      }}
    >
      {option}
    </Typography>
  );
  return live ? text : (
    <Tooltip title={NO_DATA_TOOLTIP} placement="right" arrow>
      <Box component="span" sx={{ cursor: 'not-allowed' }}>{text}</Box>
    </Tooltip>
  );
}

/** Shallow set-equality for two string arrays. */
function sameSelection(a, b) {
  if (a.length !== b.length) return false;
  const bSet = new Set(b);
  return a.every((v) => bSet.has(v));
}

const emptyDraft = () => ({
  year: [],
  months: [],
  division: [],
  state: [],
  brand: [],
  hq: [],
  bdm: [],
  zm: [],
});

/**
 * Field Sales filter bar — styled to match the Diagnosis / Efforts filter bars
 * (shared ui-kit `MultiSelectFilter`, light outlined Paper, uppercase labels
 * above each control, standard select box, Apply/Reset buttons).
 *
 * Controls (all multi-select), in order: Year, Division, Zone, State, Category,
 * Brand, DSH, BDM, ZM, HQ. Live (sent): Year (only the two real FYs are
 * selectable; other year tokens greyed), Division, State, Brand, BDM, ZM, HQ.
 * Greyed (never sent): Zone, Category, DSH.
 *
 * There is NO month control: `months` is driven by the URL only
 * (?months=2026-08, YYYY-MM → YYYYMM on the wire). No months in the URL → none
 * sent (backend default).
 */
export default function FieldSalesFilterBar() {
  const { applied, applySelection, resetSelection } = useFieldSalesFilterState();
  const { readParam, writeOwnParams } = useUrlFilters(FIELD_SALES_FILTER_KEYS);

  const {
    division: divisionOptions,
    state: stateOptions,
    brand: brandOptions,
    hq: hqOptions,
    bdm: bdmOptions,
    zm: zmOptions,
    isLoading: optionsLoading,
  } = useFieldSalesFilterOptions();

  const [draft, setDraft] = useState(() => ({
    year: [...applied.year],
    months: [...applied.months],
    division: [...applied.division],
    state: [...applied.state],
    brand: [...applied.brand],
    hq: [...applied.hq],
    bdm: [...applied.bdm],
    zm: [...applied.zm],
  }));

  // Re-seed the draft when the applied selection changes externally (e.g.
  // Reset), unless our own apply just triggered it.
  const lastAppliedRef = useRef(applied);
  const skipNextReseedRef = useRef(false);
  if (lastAppliedRef.current !== applied) {
    lastAppliedRef.current = applied;
    if (skipNextReseedRef.current) {
      skipNextReseedRef.current = false;
    } else {
      setDraft({
        year: [...applied.year],
        months: [...applied.months],
        division: [...applied.division],
        state: [...applied.state],
        brand: [...applied.brand],
        hq: [...applied.hq],
        bdm: [...applied.bdm],
        zm: [...applied.zm],
      });
    }
  }

  const setField = (field) => (next) => setDraft((prev) => ({ ...prev, [field]: next }));
  // Year only ever commits live fiscal years (greyed tokens can't be chosen).
  const setYear = (next) => setDraft((prev) => ({ ...prev, year: next.filter((y) => YEAR_LIVE.has(y)) }));

  const dirty =
    !sameSelection(draft.year, applied.year) ||
    !sameSelection(draft.months, applied.months) ||
    !sameSelection(draft.division, applied.division) ||
    !sameSelection(draft.state, applied.state) ||
    !sameSelection(draft.brand, applied.brand) ||
    !sameSelection(draft.hq, applied.hq) ||
    !sameSelection(draft.bdm, applied.bdm) ||
    !sameSelection(draft.zm, applied.zm);

  const isEmpty = (sel) =>
    sel.year.length === 0 &&
    sel.months.length === 0 &&
    sel.division.length === 0 &&
    sel.state.length === 0 &&
    sel.brand.length === 0 &&
    sel.hq.length === 0 &&
    sel.bdm.length === 0 &&
    sel.zm.length === 0;

  const isDefault = isEmpty(draft) && isEmpty(applied);

  const handleApply = () => applySelection({ ...draft });
  const handleReset = () => {
    setDraft(emptyDraft());
    resetSelection();
  };

  // ── On mount: hydrate the store from the URL's own keys (once) ─────────────
  // `months` hydrates here from ?months=YYYY-MM (URL only); there is no month
  // control. No owned key present → leave empty → no months param sent.
  const didHydrateRef = useRef(false);
  useEffect(() => {
    if (didHydrateRef.current) return;
    didHydrateRef.current = true;

    const year = splitList(readParam('fy')).filter((y) => YEAR_LIVE.has(y));
    const months = splitList(readParam('months')).filter(isMonthShape);
    const division = splitList(readParam('division'));
    const state = splitList(readParam('state'));
    const brand = splitList(readParam('brand'));
    const hq = splitList(readParam('hq'));
    const bdm = splitList(readParam('bdm'));
    const zm = splitList(readParam('zm'));

    const hasAnyUrl =
      year.length > 0 ||
      months.length > 0 ||
      division.length > 0 ||
      state.length > 0 ||
      brand.length > 0 ||
      hq.length > 0 ||
      bdm.length > 0 ||
      zm.length > 0;

    if (!hasAnyUrl) return; // leave empty → no months param → backend default

    const next = { year, months, division, state, brand, hq, bdm, zm };
    setDraft((prev) => ({ ...prev, ...next }));
    skipNextReseedRef.current = true;
    applySelection(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Write the URL whenever the APPLIED selection changes ───────────────────
  // `months` is written back as YYYY-MM so ?months survives Apply/Reload.
  useEffect(() => {
    writeOwnParams({
      fy: joinList(applied.year),
      months: joinList(applied.months),
      division: joinList(applied.division),
      state: joinList(applied.state),
      brand: joinList(applied.brand),
      hq: joinList(applied.hq),
      bdm: joinList(applied.bdm),
      zm: joinList(applied.zm),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  return (
    <Paper variant="outlined" sx={FILTER_BAR_PAPER_SX}>
      {/* Wrapping CSS grid: every filter is its own full-width cell (6 columns
          at md, 7 at lg), so the controls stretch and wrap one-per-cell rather
          than sitting at their intrinsic width. Order:
          Year, Division, Zone, State, Category, Brand, DSH, BDM, ZM, HQ, then
          Apply + Reset in the final cell. Same gap (16px) and control heights
          and label style as the shared MultiSelectFilter / EffortsFilterBar. */}
      <Box
        sx={{
          display: 'grid',
          // ONE wrapping grid: 2 cols on phones (matches Sales), 3 on small
          // tablets, then auto-fill 180px tracks from md up so controls size to
          // the row instead of a fixed column count (which previously made them
          // overlap / clip the value text at 1280px).
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            sm: 'repeat(3, minmax(0, 1fr))',
            md: 'repeat(auto-fill, minmax(180px, 1fr))',
          },
          gap: FILTER_BAR_GAP,
          alignItems: 'end',
          // Every control cell AND the control inside it must shrink to its
          // cell: force width:100% + minWidth:0 past the shared FIELD_SX
          // sm:150 floor so nothing is wider than its track.
          '& > *': { minWidth: 0, width: '100%' },
          '& > * > *': { minWidth: 0 },
        }}
      >
        <MultiSelectFilter
          label="Year"
          value={draft.year}
          options={YEAR_OPTIONS}
          onChange={setYear}
          renderOption={renderYearOption}
        />
        <MultiSelectFilter
          label="Division"
          value={draft.division}
          options={divisionOptions}
          loading={optionsLoading}
          onChange={setField('division')}
        />
        <GreyedFilter label="Zone" />
        <MultiSelectFilter
          label="State"
          value={draft.state}
          options={stateOptions}
          loading={optionsLoading}
          onChange={setField('state')}
        />
        <GreyedFilter label="Category" />
        <MultiSelectFilter
          label="Brand"
          value={draft.brand}
          options={brandOptions}
          loading={optionsLoading}
          onChange={setField('brand')}
        />
        <GreyedFilter label="DSH" />
        <MultiSelectFilter
          label="BDM"
          value={draft.bdm}
          options={bdmOptions}
          loading={optionsLoading}
          onChange={setField('bdm')}
        />
        <MultiSelectFilter
          label="ZM"
          value={draft.zm}
          options={zmOptions}
          loading={optionsLoading}
          onChange={setField('zm')}
        />
        <MultiSelectFilter
          label="HQ"
          value={draft.hq}
          options={hqOptions}
          loading={optionsLoading}
          onChange={setField('hq')}
        />

        {/* Apply + Reset occupy the last cell of the grid row. */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
          <FilterApplyButton onClick={handleApply} disabled={!dirty} />
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleReset}
            disabled={isDefault}
            sx={FILTER_BUTTON_SX}
          >
            Reset
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
